// A reply's text, read before it is drawn (chat-2, 2026-10-30). Plain
// functions, so the stream (OmniAgentContext) and the drawing
// (replyMarkdown.js) share them without the stream loading react-markdown.

// The next-actions block an older server wrote into the reply itself. The
// server now sends it as a `suggestions` event and never in the text; this
// is only so an older server's block is never shown as words.
const NEXT_BLOCK = /<next>[\s\S]*?<\/next>/gi;
const NEXT_OPEN = /<next>[\s\S]*$/i;
const NEXT_PARTIAL = /<(?:n(?:e(?:x(?:t)?)?)?)?$/i;

/**
 * The reply without any <next>…</next> block. `streaming`: also hides a block
 * still being written, down to its first letters ("<ne"), so it never flashes.
 */
export function stripNext(text, { streaming = false } = {}) {
  if (!text) return '';
  const src = String(text);
  let out = src.replace(NEXT_BLOCK, '').replace(NEXT_OPEN, '');
  if (streaming) out = out.replace(NEXT_PARTIAL, '');
  return out === src ? src : out.trimEnd();
}

// One table row: "| a | b |" -> ['a', 'b']. A pipe escaped as \| stays text.
const cellsOf = (line) => {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1);
  const cells = [];
  let cur = '';
  for (let i = 0; i < s.length; i += 1) {
    if (s[i] === '\\' && s[i + 1] === '|') { cur += '|'; i += 1; continue; }
    if (s[i] === '|') { cells.push(cur.trim()); cur = ''; continue; }
    cur += s[i];
  }
  cells.push(cur.trim());
  return cells;
};

const hasPipe = (line) => /\|/.test(line || '');
// "|---|:--:|". It must carry a pipe: a bare "---" under a line is a heading
// or a rule, not a table.
const isDelimiter = (line) => {
  if (!hasPipe(line) || !line.includes('-')) return false;
  const cells = cellsOf(line);
  return cells.length > 0 && cells.every(c => /^:?-+:?$/.test(c));
};
const alignOf = (cell) => {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  return left ? 'left' : null;
};
const FENCE = /^\s{0,3}(```|~~~)/;

/**
 * The reply as runs: { type: 'md', text } and { type: 'table', header, align,
 * rows }. A table is a row with a pipe, then a delimiter row, then rows with
 * pipes until a blank line or a line without one. Fenced blocks are left alone.
 */
export function splitBlocks(text) {
  const lines = String(text || '').split('\n');
  const blocks = [];
  let md = [];
  let inFence = false;
  const flush = () => {
    if (md.length) blocks.push({ type: 'md', text: md.join('\n') });
    md = [];
  };
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (FENCE.test(line)) inFence = !inFence;
    if (!inFence && hasPipe(line) && !isDelimiter(line) && isDelimiter(lines[i + 1])) {
      const header = cellsOf(line);
      const align = cellsOf(lines[i + 1]).map(alignOf);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && hasPipe(lines[j]) && lines[j].trim()) {
        rows.push(cellsOf(lines[j]));
        j += 1;
      }
      flush();
      blocks.push({ type: 'table', header, align, rows });
      i = j - 1;
      continue;
    }
    md.push(line);
  }
  flush();
  return blocks;
}
